import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
} from "react";
import {
  searchPresentaciones,
  createPresentacionReplicate,
  createPresentacionAvatar,
  getAvatares,
  uploadAvatar,
  getPlantillas,
  uploadPlantilla,
  getPresentacionFolders,
  createPresentacionFolder,
} from "../../api/AI/presentacionesApi";
import { useTheme } from "@mui/material/styles";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { plantillasRegistry } from "./Plantillas/registry";

import {
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  IconButton,
  Tooltip,
  Alert,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import ThreeDRotationIcon from "@mui/icons-material/ThreeDRotation";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SearchIcon from "@mui/icons-material/Search";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import PaletteIcon from "@mui/icons-material/Palette";
import BoltIcon from "@mui/icons-material/Bolt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "react-i18next";
import DialogCrearPresentacion from "./DialogCrearPresentacion";
import Visor3DPrenda from "./Visor3DPrenda";

const LIMIT_OPTIONS = [50, 100, 200];

const PresentacionesSearch = () => {
  const { contentMargin } = useContext(SidebarContext);
  const { t } = useTranslation();

  const theme = useTheme();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [limit, setLimit] = useState(100);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [avatares, setAvatares] = useState([]);
  const [selectedAvatarPath, setSelectedAvatarPath] = useState(null);
  const [loadingAvatares, setLoadingAvatares] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [plantillas, setPlantillas] = useState([]);
  const [selectedPlantilla, setSelectedPlantilla] = useState(null);
  const [loadingPlantillas, setLoadingPlantillas] = useState(false);
  const [uploadingPlantilla, setUploadingPlantilla] = useState(false);
  /** Por cada prenda seleccionada (solo con modelo): "front" | "back" */
  const [itemViews, setItemViews] = useState({});
  /** Marca para la plantilla (donde estaba ZARA KIDS) */
  const [marca, setMarca] = useState("");
  /** Motor de generación: "flux" | "gpt-image" | "nano-banana" */
  const [engine, setEngine] = useState("gpt-image");
  /** Detalle crítico del prompt (opcional) */
  const [extraPrompt, setExtraPrompt] = useState("");
  /** URL de la imagen en vista previa (null = diálogo cerrado) */
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  /** Modelo 3D seleccionado (URL OBJ) */
  const [preview3DUrl, setPreview3DUrl] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL;

  const heroPreviewItems = useMemo(
    () =>
      (results || [])
        .map((item) => {
          const path = item.path || item.payload?.path;
          if (!path) return null;
          const imageUrl =
            `${process.env.REACT_APP_API_URL}/api/nas-presentaciones` +
            path.replace("/mnt/nas/WFT2/FOTOS_WEB", "");
          const design = item.design || item.payload?.design || "Design";
          return { imageUrl, design };
        })
        .filter(Boolean)
        .slice(0, 2),
    [results],
  );

  useEffect(() => {
    if (createOpen) {
      setLoadingAvatares(true);
      setLoadingFolders(true);
      setLoadingPlantillas(true);
      getAvatares()
        .then((data) => setAvatares(data.avatares || []))
        .catch(() => setAvatares([]))
        .finally(() => setLoadingAvatares(false));
      getPresentacionFolders()
        .then((data) => setFolders(data.folders || []))
        .catch(() => setFolders([]))
        .finally(() => setLoadingFolders(false));
      getPlantillas()
        .then((data) => setPlantillas(data.plantillas || []))
        .catch(() => setPlantillas([]))
        .finally(() => setLoadingPlantillas(false));
    }
  }, [createOpen]);

  const handleSearch = async () => {
    const data = await searchPresentaciones(query, limit);
    const hits = data.points || data.hits || [];
    setResults(hits);
    setSelectedIds(new Set());
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleOpenCreate = () => {
    if (selectedIds.size === 0) return;
    setCreateResult(null);
    setSelectedAvatarPath(null);
    setSelectedPlantilla(null);
    setMarca("");
    setSelectedFolder("");
    setNewFolderName("");
    setItemViews((prev) => {
      const next = {};
      selectedIds.forEach((id) => {
        next[id] = prev[id] || "";
      });
      return next;
    });
    setEngine("gpt-image");
    setExtraPrompt("");
    setCreateOpen(true);
  };

  const setItemView = (id, view) => {
    setItemViews((prev) => ({ ...prev, [id]: view }));
  };

  const handleCreateFolder = async () => {
    const name = (newFolderName || "").trim();
    if (!name) return;
    setCreatingFolder(true);
    try {
      const data = await createPresentacionFolder(name);
      setFolders((prev) => [...prev, data.folder].sort());
      setSelectedFolder(data.folder);
      setNewFolderName("");
    } catch (err) {
      console.error("createPresentacionFolder error", err);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleAvatarFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploadingAvatar(true);
    try {
      const data = await uploadAvatar(file);
      setAvatares((prev) => [
        ...prev,
        { path: data.path, filename: data.filename },
      ]);
      setSelectedAvatarPath(data.path);
    } catch (err) {
      console.error("uploadAvatar error", err);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handlePlantillaFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setUploadingPlantilla(true);
    try {
      const data = await uploadPlantilla(file);
      setPlantillas((prev) =>
        [...prev, { path: data.path, filename: data.filename }].sort((a, b) =>
          a.filename.localeCompare(b.filename),
        ),
      );
      setSelectedPlantilla(data.filename);
    } catch (err) {
      console.error("uploadPlantilla error", err);
    } finally {
      setUploadingPlantilla(false);
    }
  };

  const handleConfirmCreate = async () => {
    const folder = (selectedFolder || "").trim();
    if (!folder) return;

    try {
      setCreating(true);

      const selectedItems = results
        .filter((item) => selectedIds.has(item.id))
        .map((item) => ({
          id: item.id,
          path: item.path || item.payload?.path,
          filename: item.filename || item.payload?.filename,
          design: item.design || item.payload?.design,
          ...(selectedAvatarPath && {
            view: itemViews[item.id] === "back" ? "back" : "front",
          }),
        }))
        .filter((x) => x.path);

      if (!selectedItems.length) {
        setCreating(false);
        setCreateOpen(false);
        return;
      }
      const templateFromRegistry = plantillasRegistry.find(
        (t) => t.id === selectedPlantilla,
      );
      const plantilla = templateFromRegistry
        ? undefined
        : selectedPlantilla || undefined;
      const templateConfig = templateFromRegistry
        ? templateFromRegistry.config
        : undefined;
      const marcaParam = (marca || "").trim() || undefined;
      const extraPromptParam = (extraPrompt || "").trim() || undefined;
      // Resolver modelo front/back a partir del nombre de archivo (_Front / _Back)
      let modelPathFront;
      let modelPathBack;
      if (selectedAvatarPath) {
        const selectedAvatar = avatares.find(
          (av) => av.path === selectedAvatarPath,
        );
        if (selectedAvatar) {
          const filename = selectedAvatar.filename;
          const match = filename.match(/^(.*)_(Front|Back)(\.[^.]+)$/i);
          if (match) {
            const base = match[1];
            const ext = match[3];
            const frontName = `${base}_Front${ext}`;
            const backName = `${base}_Back${ext}`;
            const frontAvatar = avatares.find(
              (av) => av.filename === frontName,
            );
            const backAvatar = avatares.find((av) => av.filename === backName);
            modelPathFront = frontAvatar
              ? frontAvatar.path
              : selectedAvatar.path;
            modelPathBack = backAvatar ? backAvatar.path : modelPathFront;
          } else {
            modelPathFront = selectedAvatar.path;
            modelPathBack = selectedAvatar.path;
          }
        }
      }

      const resp = modelPathFront
        ? await createPresentacionAvatar(
            modelPathFront,
            selectedItems,
            extraPromptParam,
            folder,
            plantilla,
            marcaParam,
            templateConfig,
            modelPathBack,
          )
        : await createPresentacionReplicate(
            selectedItems,
            extraPromptParam,
            folder,
            plantilla,
            marcaParam,
            templateConfig,
            engine,
          );
      setCreateResult(resp);
    } catch (err) {
      console.error("createPresentacion error", err);
      const backendError =
        err?.response?.data?.error ||
        err?.response?.data?.details?.error ||
        err?.message ||
        "error";
      setCreateResult({ error: backendError });
    } finally {
      setCreating(false);
    }
  };

  return (
    <Box
      sx={{
        ...contentMargin,
        pt: { xs: 1, md: 4 },
        mt: 0,
        mr: { xs: 0, md: 4 },
        px: { xs: 1.25, sm: 2 },
        pb: { xs: 10, sm: 3 },
        width: "100%",
        maxWidth: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
      }}
    >
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(0,188,212,0.12) 0%, rgba(103,58,183,0.12) 100%)",
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="overline"
              sx={{ opacity: 0.8, letterSpacing: 1 }}
            >
              {t("presentacionesSearch.hero.overline")}
            </Typography>
            <Typography
              variant="h4"
              sx={{ fontWeight: 800, lineHeight: 1.1, mb: 1 }}
            >
              {t("presentacionesSearch.hero.title")}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t("presentacionesSearch.hero.description")}
            </Typography>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
              <Alert severity="info" sx={{ py: 0, alignItems: "center" }}>
                {t("presentacionesSearch.hero.badgeSearch")}
              </Alert>
              <Alert severity="success" sx={{ py: 0, alignItems: "center" }}>
                {t("presentacionesSearch.hero.badgeCamera")}
              </Alert>
            </Box>
          </Grid>
          <Grid item xs={12} md={5}>
            <Card
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: 0,
              }}
            >
              <CardContent sx={{ pb: "16px !important" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    {t("presentacionesSearch.hero.whatCanYouDo")}
                  </Typography>
                  <AutoAwesomeIcon sx={{ color: "primary.main" }} />
                </Box>
                <Box sx={{ display: "grid", gap: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <SearchIcon fontSize="small" color="info" />
                    <Typography variant="body2">
                      {t("presentacionesSearch.hero.feature1")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ViewInArIcon fontSize="small" color="secondary" />
                    <Typography variant="body2">
                      {t("presentacionesSearch.hero.feature2")}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PaletteIcon fontSize="small" color="success" />
                    <Typography variant="body2">
                      {t("presentacionesSearch.hero.feature3")}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    mt: 1.5,
                    p: 1,
                    borderRadius: 1.5,
                    backgroundColor: "#070707",
                    border: "1px solid #303030",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      mb: 0.75,
                      flexWrap: "wrap",
                    }}
                  >
                    <Box
                      sx={{
                        px: 0.8,
                        py: 0.25,
                        border: "1px solid #6f6f6f",
                        color: "#d7d7d7",
                        fontSize: "0.62rem",
                      }}
                    >
                      AI board
                    </Box>
                    <Box
                      sx={{
                        px: 0.8,
                        py: 0.25,
                        border: "1px solid #6f6f6f",
                        color: "#d7d7d7",
                        fontSize: "0.62rem",
                      }}
                    >
                      Tech pack
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", gap: 0.75 }}>
                    <Box
                      sx={{
                        flex: 1,
                        minHeight: 72,
                        border: "1px solid #6f6f6f",
                        display: "grid",
                        placeItems: "center",
                        color: "#8a8a8a",
                        fontSize: "1rem",
                      }}
                    >
                      +
                    </Box>
                    <Box
                      sx={{
                        flex: 1.4,
                        minHeight: 72,
                        border: "1px solid #6f6f6f",
                        backgroundColor: "#101010",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {heroPreviewItems.length > 0 ? (
                        <Box
                          sx={{
                            height: "100%",
                            display: "grid",
                            gridTemplateColumns:
                              heroPreviewItems.length > 1 ? "1fr 1fr" : "1fr",
                            gap: 0.5,
                            p: 0.5,
                          }}
                        >
                          {heroPreviewItems.map((preview, idx) => (
                            <Box
                              key={`${preview.imageUrl}-${idx}`}
                              sx={{
                                position: "relative",
                                borderRadius: 0.75,
                                overflow: "hidden",
                                border: "1px solid #3e3e3e",
                              }}
                            >
                              <CardMedia
                                component="img"
                                image={preview.imageUrl}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  minHeight: 66,
                                  objectFit: "cover",
                                }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: 4,
                                  right: 4,
                                  bottom: 4,
                                  bgcolor: "rgba(0,0,0,0.65)",
                                  color: "#fff",
                                  px: 0.5,
                                  py: 0.2,
                                  borderRadius: 0.5,
                                  fontSize: "0.52rem",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                }}
                              >
                                {preview.design}
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                        <Box
                          sx={{
                            position: "absolute",
                            inset: 0,
                            display: "grid",
                            placeItems: "center",
                            color: "#d9d9d9",
                            fontSize: "0.63rem",
                            fontWeight: 700,
                          }}
                        >
                          design preview
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Card
        sx={{
          mb: 3,
          p: 2,
          borderRadius: 3,
          border: "1px solid",
          borderColor: "divider",
          position: "sticky",
          top: 10,
          zIndex: 1000,
          boxShadow: "0 6px 24px rgba(0,0,0,0.08)",
        }}
      >
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label={t("presentacionesSearch.search.label")}
              placeholder={t("presentacionesSearch.search.placeholder")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSearch();
              }}
              sx={{
                "& .MuiInputBase-root": {
                  backgroundColor: theme.palette.caja.main,
                  color: theme.palette.text.primary,
                },
                "& .MuiInputLabel-root": {
                  color: theme.palette.text.primary,
                },
                "& .MuiInputBase-input": {
                  color: theme.palette.text.primary,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <FormControl
                size="small"
                sx={{ minWidth: 100, flex: { xs: 1, sm: "0 0 120px" } }}
              >
                <InputLabel>
                  {t("presentacionesSearch.search.resultsLabel")}
                </InputLabel>
                <Select
                  value={limit}
                  label={t("presentacionesSearch.search.resultsLabel")}
                  onChange={(e) => setLimit(Number(e.target.value))}
                  sx={{
                    height: "56px",
                    backgroundColor: theme.palette.caja.main,
                  }}
                >
                  {LIMIT_OPTIONS.map((n) => (
                    <MenuItem key={n} value={n}>
                      {n}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleSearch}
                sx={{ height: "54px", flex: { xs: 1, sm: "1 1 120px" } }}
              >
                {t("presentacionesSearch.search.searchButton")}
              </Button>

            </Box>
          </Grid>
        </Grid>
      </Card>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
            }}
          >
            <CardContent>
              <SearchIcon sx={{ mb: 1, color: "info.main" }} />
              <Typography variant="overline" color="text.secondary">
                {t("presentacionesSearch.steps.step1Tag")}
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("presentacionesSearch.steps.step1Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("presentacionesSearch.steps.step1Desc")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
            }}
          >
            <CardContent>
              <CheckCircleIcon sx={{ mb: 1, color: "success.main" }} />
              <Typography variant="overline" color="text.secondary">
                {t("presentacionesSearch.steps.step2Tag")}
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("presentacionesSearch.steps.step2Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("presentacionesSearch.steps.step2Desc")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
            }}
          >
            <CardContent>
              <AutoGraphIcon sx={{ mb: 1, color: "secondary.main" }} />
              <Typography variant="overline" color="text.secondary">
                {t("presentacionesSearch.steps.step3Tag")}
              </Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>
                {t("presentacionesSearch.steps.step3Title")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("presentacionesSearch.steps.step3Desc")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <BoltIcon color="warning" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {t("presentacionesSearch.engines.title")}
                </Typography>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                <CheckCircleIcon
                  sx={{
                    fontSize: 16,
                    mr: 0.5,
                    verticalAlign: "text-bottom",
                    color: "warning.main",
                  }}
                />
                <strong>{t("presentacionesSearch.engines.fluxName")}:</strong>{" "}
                {t("presentacionesSearch.engines.fluxDesc")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 0.5 }}
              >
                <CheckCircleIcon
                  sx={{
                    fontSize: 16,
                    mr: 0.5,
                    verticalAlign: "text-bottom",
                    color: "warning.main",
                  }}
                />
                <strong>{t("presentacionesSearch.engines.gptName")}:</strong>{" "}
                {t("presentacionesSearch.engines.gptDesc")}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <CheckCircleIcon
                  sx={{
                    fontSize: 16,
                    mr: 0.5,
                    verticalAlign: "text-bottom",
                    color: "warning.main",
                  }}
                />
                <strong>{t("presentacionesSearch.engines.nanoName")}:</strong>{" "}
                {t("presentacionesSearch.engines.nanoDesc")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              height: "100%",
            }}
          >
            <CardContent>
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
              >
                <AutoGraphIcon color="primary" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {t("presentacionesSearch.status.title")}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {t("presentacionesSearch.status.results")}:{" "}
                <strong>{results.length}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("presentacionesSearch.status.selected")}:{" "}
                <strong>{selectedIds.size}</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("presentacionesSearch.status.limit")}:{" "}
                <strong>{limit}</strong>
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              background:
                "linear-gradient(90deg, rgba(0,188,212,0.10), rgba(255,193,7,0.10))",
            }}
          >
            <CardContent
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <AutoAwesomeIcon color="primary" />
              <Typography variant="body2" color="text.secondary">
                {t("presentacionesSearch.tip")}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {selectedIds.size > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            {t("presentacionesSearch.selection.selected")}: {selectedIds.size}
          </Typography>
          <Button size="small" onClick={clearSelection} sx={{ ml: 2 }}>
            {t("presentacionesSearch.selection.clear")}
          </Button>
          <Button
            size="small"
            variant="contained"
            sx={{ ml: 2 }}
            onClick={handleOpenCreate}
          >
            {t("presentacionesSearch.selection.createPresentation")}
          </Button>
        </Box>
      )}

      <Grid container spacing={3}>
        {results.map((item) => {
          const path = item.path || item.payload?.path;

          if (!path) return null;

          const imageUrl =
            `${process.env.REACT_APP_API_URL}/api/nas-presentaciones` +
            path.replace("/mnt/nas/WFT2/FOTOS_WEB", "");

          const design = item.design || item.payload?.design;
          const marca = item.marca || item.payload?.marca;
          const temporada = item.temporada || item.payload?.temporada;
          const color = item.color || item.payload?.color;

          const id = item.id;
          const isSelected = selectedIds.has(id);

          const d = (design || "").toUpperCase();
          const has3D =
            design &&
            (d.includes("LENGUETA_BRANDED") ||
              d.includes("LEGUETA_BRANDED") ||
              d.includes("ALOHAS_RIFE"));

          let model3dUrl = null;
          if (has3D) {
            const baseName = d.includes("ALOHAS_RIFE")
              ? "ALOHAS_RIFE"
              : "LENGUETA_BRANDED";
            model3dUrl = `${process.env.REACT_APP_API_URL}/api/nas-3d/${baseName}.obj`;
          }

          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={id}>
              <Card
                onClick={() => toggleSelect(id)}
                sx={{
                  border: isSelected
                    ? `3px solid ${theme.palette.primary.main}`
                    : "1px solid rgba(0,0,0,0.12)",
                  boxShadow: isSelected ? 4 : 1,
                  cursor: "pointer",
                }}
              >
                <Box sx={{ position: "relative" }}>
                  <CardMedia component="img" height="477" image={imageUrl} />
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setPreviewImageUrl(imageUrl);
                    }}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(255,255,255,0.8)",
                      "&:hover": { backgroundColor: "rgba(255,255,255,0.95)" },
                    }}
                    aria-label={t("presentacionesSearch.common.viewImage")}
                  >
                    <Visibility />
                  </IconButton>
                  {has3D && model3dUrl && (
                    <Tooltip title={t("presentacionesSearch.common.view3d")}>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          setPreview3DUrl(model3dUrl);
                        }}
                        sx={{
                          position: "absolute",
                          top: 8,
                          left: 8,
                          backgroundColor: "rgba(255,255,255,0.8)",
                          "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.95)",
                          },
                        }}
                        aria-label={t("presentacionesSearch.common.view3d")}
                      >
                        <ThreeDRotationIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                <CardContent>
                  <Typography variant="h6">{design}</Typography>

                  <Typography variant="body2">
                    {marca} · {temporada}
                  </Typography>

                  <Typography variant="body2">{color}</Typography>
                  {isSelected && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1,
                        color: theme.palette.primary.main,
                      }}
                    >
                      {t("presentacionesSearch.selection.selectedTag")}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Dialog
        open={Boolean(previewImageUrl)}
        onClose={() => setPreviewImageUrl(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { maxHeight: "120vh", overflow: "hidden" } }}
      >
        {previewImageUrl && (
          <CardMedia
            component="img"
            image={previewImageUrl}
            sx={{
              maxHeight: "120vh",
              objectFit: "contain",
            }}
          />
        )}
      </Dialog>

      <Dialog
        open={Boolean(preview3DUrl)}
        onClose={() => setPreview3DUrl(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { p: 2 } }}
      >
        {preview3DUrl && (
          <Box>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              {t("presentacionesSearch.preview3d")}
            </Typography>
            <Visor3DPrenda modelUrl={preview3DUrl} height={420} />
          </Box>
        )}
      </Dialog>

      <DialogCrearPresentacion
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        creating={creating}
        createResult={createResult}
        selectedIds={selectedIds}
        results={results}
        API_URL={API_URL}
        theme={theme}
        itemViews={itemViews}
        onChangeItemView={setItemView}
        folders={folders}
        selectedFolder={selectedFolder}
        onChangeFolder={setSelectedFolder}
        newFolderName={newFolderName}
        onChangeNewFolderName={setNewFolderName}
        creatingFolder={creatingFolder}
        onCreateFolder={handleCreateFolder}
        loadingFolders={loadingFolders}
        avatares={avatares}
        selectedAvatarPath={selectedAvatarPath}
        onChangeAvatarPath={setSelectedAvatarPath}
        onAvatarFile={handleAvatarFile}
        loadingAvatares={loadingAvatares}
        uploadingAvatar={uploadingAvatar}
        plantillasRegistry={plantillasRegistry}
        plantillasSubidas={plantillas}
        selectedPlantilla={selectedPlantilla}
        onChangePlantilla={setSelectedPlantilla}
        onPlantillaFile={handlePlantillaFile}
        loadingPlantillas={loadingPlantillas}
        uploadingPlantilla={uploadingPlantilla}
        marca={marca}
        onChangeMarca={setMarca}
        engine={engine}
        onChangeEngine={setEngine}
        extraPrompt={extraPrompt}
        onChangeExtraPrompt={setExtraPrompt}
        onConfirm={handleConfirmCreate}
      />
    </Box>
  );
};

export default PresentacionesSearch;
